import { gaeApp } from '../components/helpers/Analytics';
import { createBlobFromUrl } from './fileUtils';
import {
  clearAllPendingSourcePdfs,
  restorePendingSourcePdfs,
  snapshotPendingSourcePdfs
} from './pendingSourcePdfs';

const DB_NAME = 'paperfly-standalone-drafts';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';
const DRAFT_KEY = 'current';
const SAVE_DEBOUNCE_MS = 5000;

let dbPromise = null;
let saveTimer = null;
let pendingRestore = false;
let saveGeneration = 0;
let pendingGetPayload = null;

const openDb = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(
        request.error || new Error('Failed to open standalone draft cache')
      );
    };
  });

  return dbPromise;
};

const idbRequest = request =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const mapOfMapsToObject = mapOfMaps => {
  const result = {};
  if (!mapOfMaps) return result;
  for (const [docId, inner] of mapOfMaps.entries()) {
    if (inner instanceof Map) {
      const innerObj = {};
      inner.forEach((value, key) => {
        innerObj[key] = value;
      });
      result[docId] = innerObj;
    } else if (inner && typeof inner === 'object') {
      result[docId] = { ...inner };
    }
  }
  return result;
};

const mapToObject = map => {
  if (!map) return {};
  if (map instanceof Map) {
    const result = {};
    map.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  return { ...map };
};

const setToArray = set => (set instanceof Set ? Array.from(set) : []);

const objectOfObjectsToMapOfMaps = obj => {
  const result = new Map();
  if (!obj) return result;
  Object.keys(obj).forEach(docId => {
    result.set(docId, new Map(Object.entries(obj[docId] || {})));
  });
  return result;
};

const objectToMap = obj => {
  if (!obj) return new Map();
  return new Map(Object.entries(obj));
};

const serializeScannedDocuments = async scannedDocuments => {
  const result = {};
  if (!scannedDocuments) return result;

  for (const [docId, pages] of scannedDocuments.entries()) {
    if (!(pages instanceof Map)) continue;
    result[docId] = {};
    for (const [pageId, url] of pages.entries()) {
      if (!url || typeof url !== 'string') continue;
      try {
        if (url.indexOf('blob:') === 0 || url.indexOf('http') === 0) {
          const blob = await createBlobFromUrl(url);
          if (blob && blob.size > 0) {
            result[docId][pageId] = blob;
          }
        } else {
          // data URL or other string payload
          result[docId][pageId] = url;
        }
      } catch (error) {
        console.warn('Failed to serialize scanned page', docId, pageId, error);
      }
    }
  }
  return result;
};

const hydrateScannedDocuments = scannedDocuments => {
  const result = new Map();
  if (!scannedDocuments) return result;

  Object.keys(scannedDocuments).forEach(docId => {
    const pages = scannedDocuments[docId] || {};
    const pageMap = new Map();
    Object.keys(pages).forEach(pageId => {
      const value = pages[pageId];
      if (!value) return;
      if (typeof Blob !== 'undefined' && value instanceof Blob) {
        pageMap.set(pageId, URL.createObjectURL(value));
      } else if (typeof value === 'string') {
        pageMap.set(pageId, value);
      }
    });
    if (pageMap.size > 0) {
      result.set(docId, pageMap);
    }
  });
  return result;
};

const serializeDraftState = async ({
  forms,
  scannedDocuments,
  documentsStatus,
  signatures,
  successSentKeys,
  rejectedDocuments,
  usersData,
  pendingSourcePdfs
}) => ({
  forms: mapOfMapsToObject(forms),
  scannedDocuments: await serializeScannedDocuments(scannedDocuments),
  documentsStatus: mapToObject(documentsStatus),
  signatures: signatures || {},
  successSentKeys: setToArray(successSentKeys),
  rejectedDocuments: setToArray(rejectedDocuments),
  usersData: usersData || null,
  pendingSourcePdfs:
    (pendingSourcePdfs !== undefined
      ? pendingSourcePdfs
      : snapshotPendingSourcePdfs()) || []
});

const hydrateDraftState = state => ({
  forms: objectOfObjectsToMapOfMaps(state.forms),
  scannedDocuments: hydrateScannedDocuments(state.scannedDocuments),
  documentsStatus: objectToMap(state.documentsStatus),
  signatures: state.signatures || {},
  successSentKeys: new Set(state.successSentKeys || []),
  rejectedDocuments: new Set(state.rejectedDocuments || []),
  usersData: state.usersData || null,
  pendingSourcePdfs: state.pendingSourcePdfs || []
});

/**
 * Persist standalone template draft (debounced caller uses this directly).
 * @param {object} payload
 */
export const saveStandaloneDraft = async payload => {
  const {
    templateId,
    templateName,
    pwaSession,
    forms,
    scannedDocuments,
    documentsStatus,
    signatures,
    successSentKeys,
    rejectedDocuments,
    usersData,
    pendingSourcePdfs
  } = payload;

  if (!templateId || !window.draftEnabled) return;

  try {
    const state = await serializeDraftState({
      forms,
      scannedDocuments,
      documentsStatus,
      signatures,
      successSentKeys,
      rejectedDocuments,
      usersData,
      pendingSourcePdfs
    });

    const record = {
      id: DRAFT_KEY,
      templateId,
      templateName: templateName || '',
      pwaSession: pwaSession || window.pwaSession,
      updatedAt: Date.now(),
      state
    };

    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await idbRequest(tx.objectStore(STORE_NAME).put(record));
    gaeApp(
      'STANDALONE_DRAFT',
      'SAVE',
      `${templateId}:pdfs=${state.pendingSourcePdfs.length}`
    );
  } catch (error) {
    gaeApp('STANDALONE_DRAFT', 'SAVE_ERROR', templateId);
    console.warn('Failed to save standalone draft', templateId, error);
  }
};

/**
 * Schedule a draft save after SAVE_DEBOUNCE_MS of idle Redux updates.
 * @param {function(): object} getPayload
 */
export const scheduleStandaloneDraftSave = getPayload => {
  if (!window.draftEnabled) return;

  pendingGetPayload = getPayload;

  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }

  const generation = ++saveGeneration;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    if (generation !== saveGeneration) return;
    const payload = pendingGetPayload?.();
    pendingGetPayload = null;
    if (!payload?.templateId) return;
    saveStandaloneDraft({
      ...payload,
      pendingSourcePdfs: snapshotPendingSourcePdfs()
    });
  }, SAVE_DEBOUNCE_MS);
};

export const cancelScheduledStandaloneDraftSave = () => {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  pendingGetPayload = null;
  saveGeneration += 1;
};

/**
 * Immediately persist any debounced draft save.
 * @returns {Promise<boolean>} true if a save was performed
 */
export const flushScheduledStandaloneDraftSave = async () => {
  if (!window.draftEnabled) return false;

  const getPayload = pendingGetPayload;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  pendingGetPayload = null;
  saveGeneration += 1;

  if (!getPayload) return false;
  const payload = getPayload();
  if (!payload?.templateId) return false;

  await saveStandaloneDraft({
    ...payload,
    pendingSourcePdfs: snapshotPendingSourcePdfs()
  });
  return true;
};

/**
 * Whether a draft for this template exists in IndexedDB for the current PWA session.
 * @param {string} templateId
 * @returns {Promise<boolean>}
 */
export const isStandaloneDraftCachedForTemplate = async templateId => {
  if (!templateId || !window.draftEnabled) return false;
  const draft = await getStandaloneDraft();
  gaeApp(
    'STANDALONE_DRAFT_CACHE_INFO',
    `templateId: ${draft?.templateId} pwaSession: ${draft?.pwaSession}`
  );
  return Boolean(
    draft &&
      draft.templateId === templateId &&
      draft.pwaSession === window.pwaSession
  );
};

/**
 * @returns {Promise<object|null>} draft metadata + hydrated state, or null
 */
export const getStandaloneDraft = async () => {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const record = await idbRequest(tx.objectStore(STORE_NAME).get(DRAFT_KEY));
    if (!record?.templateId || !record.state) {
      gaeApp('STANDALONE_DRAFT.LOAD_MISS');
      return null;
    }

    const hydrated = {
      templateId: record.templateId,
      templateName: record.templateName || '',
      pwaSession: record.pwaSession,
      updatedAt: record.updatedAt,
      state: hydrateDraftState(record.state)
    };

    gaeApp('STANDALONE_DRAFT', 'LOAD_HIT', record.templateId);
    return hydrated;
  } catch (error) {
    gaeApp('STANDALONE_DRAFT.LOAD_ERROR');
    console.warn('Failed to read standalone draft', error);
    return null;
  }
};

/**
 * Update cached draft record pwaSession to current window.pwaSession.
 * Useful when user continues editing from a previously cached draft.
 *
 * @param {string} templateId
 */
export const updateStandaloneDraftPwaSession = async templateId => {
  if (!window.draftEnabled || !templateId) return;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const record = await idbRequest(store.get(DRAFT_KEY));
    if (!record?.templateId) return;
    if (record.templateId !== templateId) return;

    record.pwaSession = window.pwaSession;
    record.updatedAt = Date.now();
    await idbRequest(store.put(record));

    gaeApp('STANDALONE_DRAFT', 'UPDATE_PWA_SESSION', templateId);
  } catch (error) {
    console.warn('Failed to update standalone draft pwaSession', error);
  }
};

/**
 * Apply pending source PDFs from a hydrated draft into memory.
 * @param {object} draftState
 */
export const applyStandaloneDraftPendingSourcePdfs = draftState => {
  restorePendingSourcePdfs(draftState?.pendingSourcePdfs || []);
};

/**
 * Draft from a previous PWA session that should offer restore.
 * @returns {Promise<object|null>}
 */
export const getStandaloneDraftForNewSession = async () => {
  const draft = await getStandaloneDraft();
  if (!draft) return null;
  if (draft.pwaSession && draft.pwaSession === window.pwaSession) {
    return null;
  }
  return draft;
};

export const clearStandaloneDraft = async () => {
  cancelScheduledStandaloneDraftSave();
  clearAllPendingSourcePdfs();
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await idbRequest(tx.objectStore(STORE_NAME).delete(DRAFT_KEY));
    gaeApp('STANDALONE_DRAFT.CLEAR');
  } catch (error) {
    gaeApp('STANDALONE_DRAFT.CLEAR_ERROR');
    console.warn('Failed to clear standalone draft', error);
  }
};

export const markPendingStandaloneDraftRestore = () => {
  pendingRestore = true;
};

export const consumePendingStandaloneDraftRestore = () => {
  const value = pendingRestore;
  pendingRestore = false;
  return value;
};

export const hasPendingStandaloneDraftRestore = () => pendingRestore;

/**
 * One-shot /t= snapshot taken right before a forced reload (stale build).
 * Own key per template, so it never touches the standalone `current` draft.
 */
const forcedReloadKey = templateId => `forced-reload:${templateId}`;

/**
 * @param {object} payload templateId + the same state fields as a draft
 * @returns {Promise<boolean>} true if the snapshot was written
 */
export const saveForcedReloadSnapshot = async payload => {
  const { templateId } = payload;
  if (!templateId) return false;
  try {
    const state = await serializeDraftState(payload);
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await idbRequest(
      tx.objectStore(STORE_NAME).put({
        id: forcedReloadKey(templateId),
        templateId,
        updatedAt: Date.now(),
        state
      })
    );
    return true;
  } catch (error) {
    console.warn('Failed to save forced reload snapshot', templateId, error);
    return false;
  }
};

/**
 * Read and delete the snapshot of this template.
 * @param {string} templateId
 * @returns {Promise<object|null>} hydrated draft state or null
 */
export const takeForcedReloadSnapshot = async templateId => {
  if (!templateId) return null;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const record = await idbRequest(store.get(forcedReloadKey(templateId)));
    if (!record?.state) return null;
    await idbRequest(store.delete(forcedReloadKey(templateId)));
    return hydrateDraftState(record.state);
  } catch (error) {
    console.warn('Failed to read forced reload snapshot', templateId, error);
    return null;
  }
};
